import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { BadgeProps } from './badge.types';

import './badge.scss';

/**
 * The `Badge` component is used to display a count, status, or other content for UI elements.
 * The `content` can be numeric, text-based, icons, or more depending on the context.
 * The `Badge` component supports both standalone and notification usage.
 *
 * The `narrow` prop can be used to modify the padding of the badge to create a narrower appearance.
 * The `Badge` component supports various variants ('default', 'secondary', 'tertiary', 'success', 'neutral')
 * to adapt to different use-cases.
 *
 * ### Usage
 *
 * ```tsx
 * import { Badge } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return <Badge variant="default">Default</Badge>;
 * }
 * ```
 */

const Badge = forwardRef((props: BadgeProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    children,
    className,
    content,
    narrow = false,
    variant = 'default',
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  return (
    <div
      className={cn('rbui-badge', `rbui-badge--${variant}`, { 'rbui-badge--narrow-padding': narrow }, className)}
      ref={forwardedRef}
      role="status"
      {...rest}
    >
      {children}
      <div className={cn('rbui-badge__content', { 'rbui-badge__content--notification': children })}>{content}</div>
    </div>
  );
});

Badge.displayName = 'Badge';

export default Badge;
